import React, { useMemo, useRef, useState } from "react";
import { fechaMX } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Download, Printer, MessageCircle } from "lucide-react";
import { downloadCotizacionPdf, printCotizacionPdf, buildCotizacionBlob } from "@/lib/pdf";

const REP_LOGO = "/logo-cooper.png";
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

function MesAnio({ mes, anio, onMes, onAnio, anios, testid }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <Select value={String(mes)} onValueChange={(v) => onMes(Number(v))}>
        <SelectTrigger data-testid={`${testid}-mes`}><SelectValue /></SelectTrigger>
        <SelectContent>{MESES.map((mm, i) => <SelectItem key={i} value={String(i)}>{mm}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={String(anio)} onValueChange={(v) => onAnio(Number(v))}>
        <SelectTrigger data-testid={`${testid}-anio`}><SelectValue /></SelectTrigger>
        <SelectContent>{anios.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

// Reporte membretado: imprime las filas que el usuario ve en pantalla (rows ya filtradas); solo elige periodo.
//  columns: [{ header, value:(row)=>node, align, mono }] · groupBy?(row) · subtotal?(rows) · totales?(rows)
//  filtros?: [{ label, value }] -> filtros aplicados en pantalla (encabezado y nombre del archivo)
export default function ReporteModulo({
  titulo, filenameBase, rows = [], getFecha,
  columns = [], groupBy, subtotal, totales,
  filtros = [], buttonLabel = "Reporte", buttonVariant = "default", triggerTestId = "reporte-btn", prefix = "rep",
}) {
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const [open, setOpen] = useState(false);
  const nowY = new Date().getFullYear();
  const nowM = new Date().getMonth();
  const [periodo, setPeriodo] = useState("historico");
  const [mesSel, setMesSel] = useState(nowM);
  const [anioSel, setAnioSel] = useState(nowY);
  const [dMes, setDMes] = useState(0);
  const [dAnio, setDAnio] = useState(nowY);
  const [hMes, setHMes] = useState(nowM);
  const [hAnio, setHAnio] = useState(nowY);

  const aniosDisp = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { const f = getFecha(r); const d = f ? new Date(f + "T00:00:00") : null; if (d && !isNaN(d)) s.add(d.getFullYear()); });
    s.add(nowY);
    return Array.from(s).sort((a, b) => b - a);
  }, [rows, getFecha]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (periodo === "historico") return true;
    const f = getFecha(r);
    const d = f ? new Date(f + "T00:00:00") : null;
    if (!d || isNaN(d)) return false;
    const y = d.getFullYear(), mo = d.getMonth();
    if (periodo === "mesActual") return y === nowY && mo === nowM;
    if (periodo === "mes") return y === Number(anioSel) && mo === Number(mesSel);
    if (periodo === "anioActual") return y === nowY;
    if (periodo === "anioAnterior") return y === Number(anioSel);
    if (periodo === "rango") return d >= new Date(dAnio, dMes, 1) && d < new Date(hAnio, hMes + 1, 1);
    return true;
  }).sort((a, b) => (getFecha(b) || "").localeCompare(getFecha(a) || "")),
  [rows, periodo, mesSel, anioSel, dMes, dAnio, hMes, hAnio, getFecha]);

  const porGrupo = useMemo(() => {
    if (!groupBy) return [["", filtered]];
    const map = {};
    filtered.forEach((r) => { const g = groupBy(r) || "Sin asignar"; (map[g] = map[g] || []).push(r); });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, groupBy]);

  const periodoLabel =
    periodo === "mesActual" ? `${MESES[nowM]} ${nowY}` :
    periodo === "mes" ? `${MESES[mesSel]} ${anioSel}` :
    periodo === "anioActual" ? `Año ${nowY}` :
    periodo === "anioAnterior" ? `Año ${anioSel}` :
    periodo === "rango" ? `${MESES[dMes]} ${dAnio} a ${MESES[hMes]} ${hAnio}` : "Todo lo mostrado";
  const filtrosAct = filtros.filter((f) => f && f.value);
  const repFecha = fechaMX(new Date());
  const fileName = `${filenameBase} - ${periodoLabel}${filtrosAct.map((f) => ` - ${f.value}`).join("")}`;
  const totalesArr = totales ? totales(filtered) : [];
  const resumenTxt = [
    `*${titulo} · Cooper Slip*`, `Periodo: ${periodoLabel}`,
    ...filtrosAct.map((f) => `${f.label}: ${f.value}`),
    `Registros: ${filtered.length}`, ...totalesArr.map((t) => `${t.label}: ${t.value}`),
  ].filter(Boolean).join("\n");

  const guard = () => { if (!filtered.length) { toast.error("No hay registros para los filtros seleccionados."); return false; } return true; };
  const doDownload = () => { if (guard()) downloadCotizacionPdf(bodyRef.current, headerRef.current, fileName); };
  const doPrint = () => { if (guard()) printCotizacionPdf(bodyRef.current, headerRef.current, fileName); };
  const doWhatsApp = async () => {
    if (!guard()) return;
    try {
      const blob = await buildCotizacionBlob(bodyRef.current, headerRef.current);
      const file = new File([blob], `${fileName}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: titulo, text: resumenTxt });
        return;
      }
      downloadCotizacionPdf(bodyRef.current, headerRef.current, fileName);
      window.open(`https://wa.me/?text=${encodeURIComponent(resumenTxt)}`, "_blank");
      toast.success("Se descargó el PDF; adjúntalo en WhatsApp Web.");
    } catch (err) {
      if (err?.name === "AbortError") return;
      window.open(`https://wa.me/?text=${encodeURIComponent(resumenTxt)}`, "_blank");
    }
  };

  const Th = ({ c }) => <th className={`border border-gray-300 px-2 py-1 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.header}</th>;
  const Td = ({ c, r }) => <td className={`border border-gray-300 px-2 py-1 ${c.align === "right" ? "text-right" : "text-left"} ${c.mono ? "font-mono" : ""}`}>{c.value(r)}</td>;

  return (
    <>
      <Button variant={buttonVariant} onClick={() => setOpen(true)} data-testid={triggerTestId}><FileText className="h-4 w-4 mr-2" /> {buttonLabel}</Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[92dvh] overflow-y-auto" data-testid={`${prefix}-dialog`}>
          <DialogHeader>
            <DialogTitle>Reporte</DialogTitle>
            <DialogDescription>Se imprime lo que estás viendo en pantalla{filtrosAct.length ? ` (${filtrosAct.map((f) => `${f.label}: ${f.value}`).join(" · ")})` : ""}. Si quieres, acota el periodo.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Periodo</Label>
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger data-testid={`${prefix}-periodo`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mesActual">Mes en curso</SelectItem>
                  <SelectItem value="mes">Mes específico</SelectItem>
                  <SelectItem value="anioActual">Año en curso ({nowY})</SelectItem>
                  <SelectItem value="anioAnterior">Un año específico</SelectItem>
                  <SelectItem value="rango">Rango personalizado</SelectItem>
                  <SelectItem value="historico">Todo lo mostrado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {periodo === "mes" && (
              <div className="space-y-2"><Label>Mes y año</Label><MesAnio mes={mesSel} anio={anioSel} onMes={setMesSel} onAnio={setAnioSel} anios={aniosDisp} testid={`${prefix}-mesesp`} /></div>
            )}
            {periodo === "anioAnterior" && (
              <div className="space-y-2">
                <Label>Año</Label>
                <Select value={String(anioSel)} onValueChange={(v) => setAnioSel(Number(v))}>
                  <SelectTrigger data-testid={`${prefix}-anio`}><SelectValue /></SelectTrigger>
                  <SelectContent>{aniosDisp.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {periodo === "rango" && (
              <>
                <div className="space-y-2"><Label>Desde</Label><MesAnio mes={dMes} anio={dAnio} onMes={setDMes} onAnio={setDAnio} anios={aniosDisp} testid={`${prefix}-desde`} /></div>
                <div className="space-y-2"><Label>Hasta</Label><MesAnio mes={hMes} anio={hAnio} onMes={setHMes} onAnio={setHAnio} anios={aniosDisp} testid={`${prefix}-hasta`} /></div>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="rounded-md bg-muted px-3 py-2 text-sm" data-testid={`${prefix}-resumen`}><span className="font-medium">{filtered.length}</span> registro(s)</div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={doPrint} data-testid={`${prefix}-imprimir`}><Printer className="h-4 w-4 mr-1.5" /> Imprimir</Button>
              <Button variant="outline" size="sm" onClick={doDownload} data-testid={`${prefix}-descargar`}><Download className="h-4 w-4 mr-1.5" /> Descargar</Button>
              <Button variant="outline" size="sm" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" onClick={doWhatsApp} data-testid={`${prefix}-whatsapp`}><MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp</Button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Vista previa del documento:</p>

          <div className="overflow-x-auto bg-gray-100 p-3 rounded-md" data-testid={`${prefix}-preview`}>
            <div className="bg-white text-black mx-auto shadow-sm box-border" style={{ width: 750 }}>
              <style>{`.pdf-exporting table td,.pdf-exporting table th{line-height:1.4!important;padding-top:4px!important;padding-bottom:4px!important}.pdf-exporting tr{page-break-inside:avoid;break-inside:avoid}`}</style>
              <div ref={headerRef} className="px-8 pt-6">
                <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-start bg-gray-50 px-3 py-3">
                  <div className="text-center">
                    <img src={REP_LOGO} className="h-16 w-16 object-contain rounded-lg mx-auto" alt="Cooper Slip" />
                    <div className="text-xs mt-1">Cooper Slip</div>
                  </div>
                  <div className="text-center text-sm font-bold leading-snug pt-3">
                    <p>Cuando tu cliente queda bien, Tu quedas mejor.</p>
                    <p>Elije las duelas que respaldan a tu negocio</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-3xl font-extrabold tracking-tight uppercase">{titulo}</h2>
                    <p className="text-sm mt-1"><span className="font-bold">Fecha:</span> {repFecha}</p>
                  </div>
                </div>
                <div className="border-t-4 border-black" />
              </div>
              <div ref={bodyRef} className="px-8 pt-4 pb-16">
                <p className="text-sm mb-4 avoid-break">
                  <span className="font-bold">Periodo:</span> {periodoLabel}
                  {filtrosAct.map((f, i) => <React.Fragment key={i}> &nbsp;·&nbsp; <span className="font-bold">{f.label}:</span> {f.value}</React.Fragment>)}
                </p>
                {filtered.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay registros para los filtros seleccionados.</p>
                ) : (
                  porGrupo.map(([g, list], gi) => (
                    <div key={gi} className="mb-5">
                      {g && <h3 className="text-sm font-bold bg-gray-100 border border-gray-300 px-2 py-1 avoid-break">{g}</h3>}
                      <table className="w-full text-xs border-collapse">
                        <thead><tr className="bg-gray-50">{columns.map((c, i) => <Th key={i} c={c} />)}</tr></thead>
                        <tbody>{list.map((r, ri) => <tr key={ri}>{columns.map((c, i) => <Td key={i} c={c} r={r} />)}</tr>)}</tbody>
                      </table>
                      {subtotal && g && <div className="text-right text-xs font-semibold mt-1 avoid-break">Subtotal {g}: {subtotal(list)}</div>}
                    </div>
                  ))
                )}
                {filtered.length > 0 && totalesArr.length > 0 && (
                  <div className="mt-6 border-t-2 border-black pt-2 text-sm avoid-break" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                    {totalesArr.map((t, i) => (
                      <div key={i} className="flex justify-between"><span>{t.label}</span><span className="font-bold">{t.value}</span></div>
                    ))}
                    <p className="text-xs text-gray-500 mt-2">Registros: {filtered.length}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
